<script lang="ts">
	import { get } from 'svelte/store';
	//Formating
	import { format } from 'svelte-i18n';
	// Design system components
	import {
		Button,
		ButtonGroup,
		Container,
		IconButton,
		Modal,
		Text,
	} from '@Shelly/shelly-design-system';
	// Helpers
	import { save_home } from 'src/helpers/home_helpers';
	import { home_data } from 'src/stores/account/user';
	import { deletePvSetup, getPVSetupTypes } from './pv_setup_helper';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	//Components
	import PVSetupConfig from './PVSetupConfig.svelte';
	import PVSetupInfoModal from './PVSetupInfoModal.svelte';
	//Types
	import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
	import type { PVSetupTypes } from './pv_setup_helper';
	import type { Device } from 'src/types/Device';

	export let pvSetupType: PVSetupTypes;
	export let pvSetupDevices: { gridDevices: Device[]; solarDevices: Device[] };

	const onFinishSetup = (e: CustomEvent) => {
		pvSetupType = null;
		pvSetupType = e.detail;
	};

	const savePVSetupType = async (type: string) => {
		await save_home('net_metering_type', type);
	};

	const isPVSetupType = (value: string): value is PVSetupTypes => {
		return ['Parallel', 'BackUP', 'BalconyPV'].includes(value);
	};

	const deletePVSetupType = async () => {
		waitForResponse = true;
		const _pvSetupType = pvSetupType;
		const pvConfigurations = get(home_data)?.pv_configurations || {};
		delete pvConfigurations[pvSetupType];
		const res: CloudApiResponse =
			Object.keys(pvConfigurations).length === 0
				? await deletePvSetup()
				: await save_home('pv_configurations', pvConfigurations);
		if (res?.isok) {
			showDeleteModal = false;
			pvSetupType = null;
			pvSetupType = _pvSetupType;
		} else {
			onSettingsResponse(res);
		}
		waitForResponse = false;
	};

	let showConfigWizard = false;
	let showDeleteModal = false;
	let showInfoModal = false;
	let waitForResponse = false;
</script>

<div class="d-flex justify-content-between align-items-center mb-m">
	<ButtonGroup aria-label="Button group">
		{#each getPVSetupTypes() as type}
			<Button
				id="token-ucg0vt5"
				size="m"
				class={pvSetupType == type.title && 'active'}
				on:click={() => {
					if (isPVSetupType(type.title)) {
						pvSetupType = type.title;
						savePVSetupType(type.title);
					} else {
						console.error('Invalid PVSetupType:', type.title);
					}
				}}
			>
				{type.translation}
			</Button>
		{/each}
	</ButtonGroup>
	<div class="d-flex">
		<IconButton
			aria-label={$format('button_info', { default: 'Button info' })}
			class="mr-s"
			icon="fas fa-circle-info"
			variant="help"
			on:click={() => (showInfoModal = true)}
		/>
		<IconButton
			class="mr-s"
			variant="danger"
			icon="fas fa-trash"
			id="pv-setup-settings-btn"
			aria-label={$format('button_pv_setup_settings', {
				default: 'PV setup settings',
			})}
			loading={waitForResponse}
			disabled={waitForResponse ||
				!(pvSetupDevices?.gridDevices?.length && pvSetupDevices?.solarDevices?.length)}
			on:click={() => (showDeleteModal = true)}
		/>
		<IconButton
			class="mb-s"
			id="pv-setup-settings-delete-btn"
			aria-label={$format('button_delete', {
				default: 'Delete button',
			})}
			icon="fas fa-gear"
			on:click={() => (showConfigWizard = true)}
		/>
	</div>
</div>

{#if pvSetupDevices?.gridDevices?.length == 0 || pvSetupDevices?.solarDevices?.length == 0}
	<div class="d-flex justify-content-center">
		<Button
			class="align-self-center text-capitalize mt-m"
			size="l"
			on:click={() => {
				showConfigWizard = true;
			}}
		>
			{$format('pv_setup_button_configurations', {
				default: 'Set up PV configurations',
			})}
		</Button>
	</div>
{/if}

{#if showConfigWizard}
	<PVSetupConfig type={pvSetupType} bind:showModal={showConfigWizard} on:save={onFinishSetup} />
{:else if showDeleteModal}
	<Modal
		showModal={showDeleteModal}
		on:close={() => (showDeleteModal = false)}
		title={$format('button_delete', { default: 'Delete' })}
		><svelte:fragment slot="content">
			<Container padding="0">
				<Text class="mb-s">
					{$format('modal_pv_setup_delete_msg', {
						default: 'Are you sure you want to delete this PV Set-up ?',
					})}
				</Text>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showDeleteModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => deletePVSetupType()}
			>
				{$format('button_delete', { default: 'Delete' })}
			</Button>
		</div>
	</Modal>
{:else if showInfoModal}
	<PVSetupInfoModal bind:showModal={showInfoModal} />
{/if}
